import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Tiny singleton fetcher for editable landing/site copy. Fetches once per
 * page and shares the promise across every component that mounts.
 * Returns `values[key]` when present, else `fallback`. Never blocks render.
 */
let _cache = null;
let _pending = null;
const _subs = new Set();

function _fetch() {
  if (_pending) return _pending;
  _pending = api.get("/site/copy").then((r) => {
    _cache = r.data.values || {};
    _pending = null;
    _subs.forEach((cb) => cb(_cache));
  }).catch(() => {
    _cache = {};
    _pending = null;
    _subs.forEach((cb) => cb(_cache));
  });
  return _pending;
}

export function primeSiteCopy() { return _fetch(); }
export function invalidateSiteCopy() {
  _cache = null; _pending = null; _fetch();
}

export default function useSiteCopy(key, fallback = "") {
  const [value, setValue] = useState(() => (_cache ? (_cache[key] ?? fallback) : fallback));

  useEffect(() => {
    if (_cache) { setValue(_cache[key] ?? fallback); return; }
    const cb = (values) => setValue(values[key] ?? fallback);
    _subs.add(cb);
    _fetch();
    return () => { _subs.delete(cb); };
  }, [key, fallback]);

  return value;
}
